import React, { Fragment, useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import Container from '@material-ui/core/Container';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import PlayerControls from './PlayerControls';
import screenfull from 'screenfull';
import { Typography } from '@material-ui/core';

const useStyles = makeStyles({
  playerWrapper: {
    width: '100%',
    position: 'relative',
  },
});

const format = (seconds) => {
  if (isNaN(seconds)) {
    return '00:00';
  }

  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date.getUTCSeconds().toString().padStart(2, '0');

  if (hh) {
    return `${hh}:${mm.toString.padStart(2, '0')}:${ss}`;
  }

  return `${mm}:${ss}`;
};

let count = 0;
export default function VideoPlayer({...props}) {
  const classes = useStyles();
  const [videoState, setVideoState] = useState({
    playing: false,
    muted: true,
    volume: 0.6,
    playbackrate: 1.0,
    miniplayer: false,
    played: 0,
    seeking: false,
    loop: true,
    buffering: false,
    error: false,
    fullscreen: false,
  });

  const [timeDisplayFormat, setTimeDisplayFormat] = useState('normal');

  const [bookmarks, setBookmarks] = useState([]);

  const {
    playing,
    muted,
    volume,
    playbackrate,
    miniplayer,
    played,
    seeking,
    loop,
    buffering,
    error,
    fullscreen,
  } = videoState;

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const controlsRef = useRef(null);

  const handlePlayPause = () => {
    setVideoState({ ...videoState, playing: !videoState.playing });
  };

  const handleRewind = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() - 10);
  };

  const handleFastForward = () => {
    playerRef.current.seekTo(playerRef.current.getCurrentTime() + 10);
  };

  const handleMute = () => {
    setVideoState({ ...videoState, muted: !videoState.muted });
  };

  const handleVolumeChange = (e, newValue) => {
    setVideoState({
      ...videoState,
      volume: parseFloat(newValue / 100),
      muted: newValue === 0 ? true : false,
    });
  };

  const handleVolumeSeekUp = (e, newValue) => {
    setVideoState({
      ...videoState,
      volume: parseFloat(newValue / 100),
      muted: newValue === 0 ? true : false,
    });
  };

  const handlePlaybackRateChange = (rate) => {
    setVideoState({ ...videoState, playbackrate: rate });
  };

  const handleToggleFullScreen = () => {
    setVideoState({ ...videoState, fullscreen: !fullscreen });
    screenfull.toggle(playerContainerRef.current);
  };

  const handleTogglePip = () => {
    setVideoState({ ...videoState, miniplayer: !videoState.miniplayer });
  };

  const handleProgress = (changeState) => {
    if (count > 0.2) {
      controlsRef.current.style.visibility = 'hidden';
      count = 0;
    }

    if (controlsRef.current.style.visibility === 'visible') {
      count += 1;
    }
    if (!seeking) {
      setVideoState({ ...videoState, ...changeState });
    }
  };

  const handleSeekChange = (e, newValue) => {
    setVideoState({ ...videoState, played: parseFloat(newValue / 100) });
  };

  const handleSeekMouseDown = (e) => {
    setVideoState({ ...videoState, seeking: true });
  };

  const handleSeekMouseUp = (e, newValue) => {
    setVideoState({ ...videoState, seeking: false });
    playerRef.current.seekTo(newValue / 100);
  };

  const handleChangeDisplayFormat = () => {
    setTimeDisplayFormat(
      timeDisplayFormat === 'normal' ? 'remaining' : 'normal'
    );
  };

  const handleBookmark = () => {
    const canvas = canvasRef.current;
    canvas.width = 160;
    canvas.height = 90;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      playerRef.current.getInternalPlayer(),
      0,
      0,
      canvas.width,
      canvas.height
    );

    const bookmarkUrl = canvas.toDataURL();
    canvas.width = 0;
    canvas.height = 0;

    setBookmarks([
      ...bookmarks,
      { time: currentTime, display: elapsedTime, image: bookmarkUrl },
    ]);
  };

  const handleMouseMove = () => {
    controlsRef.current.style.visibility = 'visible';
    count = 0;
  };

  const handleLoop = () => {
    setVideoState({ ...videoState, loop: !videoState.loop });
  };

  const handleBuffer = () => {
    controlsRef.current.style.visibility = 'visible';
    setVideoState({ ...videoState, buffering: true });
  };

  const handleBufferEnd = () => {
    controlsRef.current.style.visibility = 'hidden';
    setVideoState({ ...videoState, buffering: false });
  };

  const handleError = () => {
    setVideoState({ ...videoState, error: true });
  };
  const currentTime = playerRef.current
    ? playerRef.current.getCurrentTime()
    : '00:00';

  const duration = playerRef.current
    ? playerRef.current.getDuration()
    : '00:00';

  const elapsedTime =
    timeDisplayFormat === 'normal'
      ? format(currentTime)
      : `-${format(duration - currentTime)}`;
  const totalDuration = format(duration);

  return (
    <Fragment>
      <Container maxWidth='md'>
        <div
          ref={playerContainerRef}
          className={classes.playerWrapper}
          onMouseMove={handleMouseMove}
        >
          <ReactPlayer
            ref={playerRef}
            width='100%'
            height='100%'
           url={props.videoUrl}
           config={{
            file: {
              attributes: {
                crossOrigin: 'anonymous',
                style: props.isMobile ? {
                  borderRadius:'0.5rem',
                  width:'100%',
                  height:'100%',
                }:{
                  borderRadius:'0.5rem',
                  width:'100%',
                  height:'100%',
                  minWidth:'600px',
                  minHeight:'400px'
                },
                controlsList: 'nodownload'
              },
            },
          }}
            muted={muted}
            playing={playing}
            volume={volume}
            playbackRate={playbackrate}
            pip={miniplayer}
            stopOnUnmount={true}
            onProgress={handleProgress}
            onContextMenu={e => e.preventDefault()}
            loop={loop}
            onBuffer={handleBuffer}
            onBufferEnd={handleBufferEnd}
            onError={handleError}
          />
          <PlayerControls
            ref={controlsRef}
            onRewind={handleRewind}
            onFastForward={handleFastForward}
            onPlayPause={handlePlayPause}
            onMute={handleMute}
            onVolumeChange={handleVolumeChange}
            onVolumeSeekUp={handleVolumeSeekUp}
            onPlaybackRateChange={handlePlaybackRateChange}
            onToggleFullScreen={handleToggleFullScreen}
            onTogglePip={handleTogglePip}
            onSeek={handleSeekChange}
            onSeekMouseDown={handleSeekMouseDown}
            onSeekMouseUp={handleSeekMouseUp}
            onChangeDisplayFormat={handleChangeDisplayFormat}
            onBookmark={handleBookmark}
            onLoop={handleLoop}
            playing={playing}
            muted={muted}
            volume={volume}
            playbackRate={playbackrate}
            played={played}
            elapsedTime={elapsedTime}
            totalDuration={totalDuration}
            loop={loop}
            buffering={buffering}
            error={error}
            fullscreen={fullscreen}
            videoUrl={props.videoUrl}
            isMobile={props.isMobile}
          />
        </div>
      </Container>
    </Fragment>
  );
}